import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sriBrowserPoolApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";
import { SriBrowserPoolI } from "../../interfaces";

const getPools = async (): Promise<{ data: SriBrowserPoolI[] }> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await sriBrowserPoolApi.get("", {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const createPool = async (payload: {
  nombre: string;
  cdpEndpoint: string;
  maxConcurrent?: number;
}) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await sriBrowserPoolApi.post("", payload, {
    headers: { Authorization: t },
  });
  return data;
};

export const updatePool = async (
  id: number,
  payload: Partial<{
    nombre: string;
    cdpEndpoint: string;
    maxConcurrent: number;
    activo: boolean;
  }>,
) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await sriBrowserPoolApi.patch(`/${id}`, payload, {
    headers: { Authorization: t },
  });
  return data;
};

export const deletePool = async (id: number) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await sriBrowserPoolApi.delete(`/${id}`, {
    headers: { Authorization: t },
  });
  return data;
};

/** Usado desde el formulario de cliente para asignar/desasignar el pool. */
export const asignarClienteAPool = async (
  clientId: number,
  poolId: number | null,
) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await sriBrowserPoolApi.post(
    "/asignar",
    { clientId, poolId },
    { headers: { Authorization: t } },
  );
  return data;
};

export const useSriBrowserPools = () => {
  const queryClient = useQueryClient();

  const poolsQuery = useQuery(["sri-browser-pools"], () => getPools(), {
    refetchOnWindowFocus: false,
  });

  const createPoolMutation = useMutation(createPool, {
    onSuccess: () => queryClient.invalidateQueries(["sri-browser-pools"]),
  });

  const updatePoolMutation = useMutation(
    ({ id, payload }: { id: number; payload: any }) => updatePool(id, payload),
    { onSuccess: () => queryClient.invalidateQueries(["sri-browser-pools"]) },
  );

  const deletePoolMutation = useMutation(deletePool, {
    onSuccess: () => queryClient.invalidateQueries(["sri-browser-pools"]),
  });

  return {
    poolsQuery,
    createPoolMutation,
    updatePoolMutation,
    deletePoolMutation,
  };
};

/**
 * Hook liviano para usar en el Select del formulario de cliente — solo
 * trae {value, label} de los pools activos, sin toda la data de gestión.
 */
export const usePoolOptions = () => {
  const { data, isLoading } = useQuery(
    ["sri-browser-pools-options"],
    () => getPools(),
    { refetchOnWindowFocus: false },
  );

  const options = (data?.data ?? []).map((p) => ({
    value: p.id,
    label: `${p.nombre} (${p.clientes} cliente${p.clientes === 1 ? "" : "s"})`,
  }));

  return { options, isLoading };
};
